import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router, RouterStateSnapshot } from '@angular/router';
import {
  OnboardingData,
  OnboardingService,
} from '@app-shared/services/onboarding/onboarding.service';
import { lastValueFrom, take } from 'rxjs';
import { CreateInitialEntitiesCommand } from './commands/create-initial-entities.command';
import { Auth, user, User } from '@angular/fire/auth';
import { FlowNavigatorService } from './services/flow-navigator.service';
import { EntryParamsService } from './services/entry-params.service';

@Injectable()
export class OnboardingDataResolver implements Resolve<OnboardingData | null> {
  private onboardingService = inject(OnboardingService);
  private flowService = inject(FlowNavigatorService);
  private router = inject(Router);

  private firebaseAuth = inject(Auth);
  private createInitialEntitiesCommand = inject(CreateInitialEntitiesCommand);
  private entryParams = inject(EntryParamsService);

  private user$ = user(this.firebaseAuth);

  async resolve(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<OnboardingData | null> {
    // Ad links carry ?previousFundID and can land on any flow route (normally
    // goal). Capture it before entity creation / save below consumes it.
    this.entryParams.capturePreviousFundId(state.root.queryParamMap.get('previousFundID'));

    let user: User | null;
    try {
      user = await lastValueFrom(this.user$.pipe(take(1)));
      if (user === null) {
        return null;
      }
    } catch (e) {
      return null;
    }

    // load the data
    let data: OnboardingData;

    try {
      data = await this.onboardingService.loadOnboardingData(user!.uid);

      // If existing doc is missing person_id (bad state from interrupted
      // entity creation), re-run entity creation to repair it
      if (!this.onboardingService.personId) {
        await this.createInitialEntitiesCommand.execute({
          uid: user!.uid,
        });
      }

      // Always-overwrite: the latest ?previousFundID wins, so a marketing
      // re-engagement link from a new provider correctly updates attribution.
      const seedFundId = this.entryParams.consumePreviousFundId();
      if (seedFundId) {
        this.onboardingService.previous_fund_id = seedFundId;
        await this.onboardingService.save();
      }

      // Restore flow navigator position so Next/Back buttons work after refresh.
      // last_page_path records the page the user was on BEFORE clicking Next,
      // so the page they actually reached is one step ahead.
      const currentUrl = state.url.split('?')[0];
      const savedPath = data.last_page_path;

      if (currentUrl.startsWith('/kiwisaver/onboarding/flow')) {
        // User refreshed on a flow page — restore position from the actual URL
        this.flowService.setPositionFromRouteURL(currentUrl);
      } else if (savedPath && currentUrl === '/kiwisaver/onboarding') {
        // User landed on the Welcome page but has progress. A saved page that no
        // longer exists resumes AT its replacement (they were on that screen);
        // otherwise redirect to the page after last_page_path (that's where they
        // actually were). An unknown saved path leaves them on Welcome.
        const replacement = this.flowService.resolveLegacyPath(savedPath);
        const resumePath =
          replacement !== savedPath ? replacement : this.flowService.getNextPathFrom(savedPath);
        if (resumePath) {
          this.router.navigate([resumePath]);
        }
      }

      return data;
    } catch (e) {
      await this.createInitialEntitiesCommand.execute({
        uid: user!.uid,
      });

      return null;
    }
  }
}
